/*
  Light DOM web components for the Groovy Girls coloring-pages promo, both rendered by
  sections/coloring-promo.liquid (one block each): <coloring-promo-bar> (sticky bar under the
  header) and <coloring-promo-badge> (widget docked in a bottom corner).
  Tickets: AS-1218525607690489, AS-1218997118229944, AS-1218991636923910
*/

const inDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);
const findHeaderSection = () => document.querySelector('.header-section');
const findHeader = () => document.querySelector('.header-section .header');

let currentBar = null;

class ColoringPromoBar extends HTMLElement {
  constructor() {
    super();
    this.moving = false;
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
    this.updateHeaderHeight = this.updateHeaderHeight.bind(this);
    this.updateFooterHeight = this.updateFooterHeight.bind(this);
  }

  static get storageKey() {
    return 'coloring-promo-bar:dismissed';
  }

  connectedCallback() {
    if (this.moveNextToHeader()) return;

    currentBar = this;
    this.closeButton = this.querySelector('[data-coloring-bar-close]');
    this.header = findHeader() || document.querySelector('header');
    this.footer = document.querySelector('.footer-group');

    // In the theme editor a dismissal must never stick, or the merchant can't get the block back
    // to edit it — same reason the theme's ModalElement re-opens on shopify:section:select.
    if (inDesignMode()) {
      this.addEventListener('shopify:block:select', this.onBlockSelect);
    }
    else if (this.isDismissed()) {
      this.hide();
      return;
    }

    this.closeButton?.addEventListener('click', this.onCloseClick);

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.updateHeight);
      this.resizeObserver.observe(this);

      if (this.header) {
        this.headerResizeObserver = new ResizeObserver(this.updateHeaderHeight);
        this.headerResizeObserver.observe(this.header, { box: 'border-box' });
      }

      if (this.footer) {
        this.footerResizeObserver = new ResizeObserver(this.updateFooterHeight);
        this.footerResizeObserver.observe(this.footer, { box: 'border-box' });
      }
    }
    else {
      window.addEventListener('resize', this.updateHeight);
      window.addEventListener('resize', this.updateHeaderHeight);
      window.addEventListener('resize', this.updateFooterHeight);
    }

    this.updateHeight();
    this.updateHeaderHeight();
    this.updateFooterHeight();
  }

  // On desktop the theme takes the footer out of flow (.footer-group is position:absolute, z-index
  // -1, drawn behind the page), so <body> ends where the footer starts. Sticky elements are released
  // at their parent's bottom, which let the bar get cut in half at the footer. The footer's height
  // feeds a padding on <body> (see the CSS) so <body> reaches the real end of the scroll.
  updateFooterHeight() {
    if (!this.footer) return;

    document.documentElement.style.setProperty('--coloring-footer-height', `${this.footer.getBoundingClientRect().height.toFixed(1)}px`);
  }

  /*
    The bar is moved to sit right after the header's sticky wrapper (.header-section), as its
    sibling in <body>, and is sticky there itself. Both then share <body> as containing block, so
    the bar stays under the header to the end of the page. Left in <main>, it was released when
    <main> ended and slid under the header, cut in half, at the footer.
    It is deliberately a sibling, not a child: inside .header-section it made that sticky wrapper
    taller (pushing the header off the top at the footer, since this theme's reveal footer ends
    <body> early), and hung off it as overflow Chrome's GPU compositor intermittently left one row
    of stale pixels at the seam.
    Returns true when it moved, since the move itself re-runs connectedCallback.
  */
  moveNextToHeader() {
    const headerSection = findHeaderSection();
    if (!headerSection || this.previousElementSibling === headerSection) return false;

    this.homeSectionId = this.closest('.shopify-section')?.id;
    document.querySelectorAll('.header-section ~ coloring-promo-bar').forEach((bar) => bar.remove());

    this.moving = true;
    headerSection.after(this);
    this.moving = false;
    return true;
  }

  disconnectedCallback() {
    if (this.moving) return;

    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.resizeObserver?.disconnect();
    this.headerResizeObserver?.disconnect();
    this.footerResizeObserver?.disconnect();
    window.removeEventListener('resize', this.updateHeight);
    window.removeEventListener('resize', this.updateHeaderHeight);
    window.removeEventListener('resize', this.updateFooterHeight);
    document.documentElement.style.removeProperty('--coloring-bar-height');
    document.documentElement.style.removeProperty('--coloring-header-height');
    document.documentElement.style.removeProperty('--coloring-footer-height');
  }

  // Applied synchronously, not in a rAF: ResizeObserver callbacks run after layout but before
  // paint, so setting the var here lands in the same frame as the header's resize. Deferring it
  // left the bar one frame behind the header's shrink/grow — a visible ~50px jump each time.
  updateHeaderHeight() {
    if (!this.header) return;

    document.documentElement.style.setProperty('--coloring-header-height', `${this.header.getBoundingClientRect().height.toFixed(1)}px`);
  }

  isDismissed() {
    try {
      return window.sessionStorage.getItem(this.constructor.storageKey) === 'true';
    }
    catch (error) {
      return false;
    }
  }

  onCloseClick() {
    if (!inDesignMode()) {
      try {
        window.sessionStorage.setItem(this.constructor.storageKey, 'true');
      }
      catch (error) {
        // sessionStorage unavailable (private browsing, etc.) — hides for this page view only
      }
    }

    this.hide();
  }

  onBlockSelect() {
    this.hidden = false;
    this.updateHeight();
  }

  hide() {
    this.hidden = true;
    document.documentElement.style.setProperty('--coloring-bar-height', '0px');
  }

  updateHeight() {
    if (this.hidden) return;

    document.documentElement.style.setProperty('--coloring-bar-height', `${this.getBoundingClientRect().height.toFixed(1)}px`);
  }
}

if (!customElements.get('coloring-promo-bar')) {
  customElements.define('coloring-promo-bar', ColoringPromoBar);

  // Theme editor only: the bar sits outside its own section, so the editor's re-renders need a
  // hand. Header re-rendered and the bar lost → put it back after the header wrapper. Our section
  // removed or re-rendered → drop the old bar (a re-render brings a new one, which moves itself).
  if (inDesignMode()) {
    document.addEventListener('shopify:section:unload', (event) => {
      if (currentBar && event.target.id === currentBar.homeSectionId) {
        currentBar.remove();
        currentBar = null;
      }
    });

    document.addEventListener('shopify:section:load', () => {
      if (currentBar && !currentBar.isConnected) findHeaderSection()?.after(currentBar);
    });
  }
}

class ColoringPromoBadge extends HTMLElement {
  constructor() {
    super();
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
  }

  static get dismissedKey() {
    return 'coloring-promo-badge:dismissed';
  }

  connectedCallback() {
    this.closeButton = this.querySelector('[data-coloring-badge-close]');

    // In the theme editor a dismissal must never stick, or the merchant can't get the block back.
    if (inDesignMode()) {
      this.addEventListener('shopify:block:select', this.onBlockSelect);
    }
    else if (this.isDismissed()) {
      this.hidden = true;
      return;
    }

    this.closeButton?.addEventListener('click', this.onCloseClick);
  }

  disconnectedCallback() {
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  isDismissed() {
    try {
      return window.sessionStorage.getItem(this.constructor.dismissedKey) === 'true';
    }
    catch (error) {
      return false;
    }
  }

  onCloseClick() {
    if (!inDesignMode()) {
      try {
        window.sessionStorage.setItem(this.constructor.dismissedKey, 'true');
      }
      catch (error) {
        // sessionStorage unavailable — hides for this page view only
      }
    }

    this.hidden = true;
  }

  onBlockSelect() {
    this.hidden = false;
  }
}

if (!customElements.get('coloring-promo-badge')) {
  customElements.define('coloring-promo-badge', ColoringPromoBadge);
}

/*
  Klaviyo signup (ticket AS-1218991636923910). When the section has a Klaviyo form ID, the bar
  and badge render as <button data-coloring-signup="{formId}"> instead of download links.

  - Open: `_klOnsite.push(['openForm', id])` — Klaviyo's documented custom trigger. It queues until
    klaviyo.js has loaded, so it also works when the consent gate replays the click right after the
    visitor accepts cookies. Without consent the gate intercepts the click before this runs.
  - Download: Klaviyo dispatches `klaviyoForms` on window, with `detail.type === 'submit'` once per
    successful submission and `detail.formId`. Any form listed in the section's download IDs
    (the signup form plus e.g. the timed popup) releases the PDF, on the page, same-origin so the
    browser downloads it instead of opening it (see pdf_href in the section).
  - Fallback: if Klaviyo never opens the form (klaviyo.js stopped by an ad blocker, the form not
    live), the click would otherwise do nothing. When no `open` event for that form arrives within
    OPEN_TIMEOUT_MS, the PDF downloads directly — the pre-signup behavior.
  Both are delegated from document, so they are bound once however often the section re-renders.
  The PDF comes from the first bar/badge on the page; both blocks should point to the same file.
*/
const OPEN_TIMEOUT_MS = 4000;
let pendingOpen = null;

const findPdfSource = () => {
  const source = document.querySelector('coloring-promo-bar[data-pdf-url], coloring-promo-badge[data-pdf-url]');
  return source?.dataset.pdfUrl ? source : null;
};

const downloadPdf = (url) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = '';
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
};

if (!window.coloringPromoSignupBound) {
  window.coloringPromoSignupBound = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-coloring-signup]');
    if (!trigger) return;

    const formId = trigger.dataset.coloringSignup;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);

    clearTimeout(pendingOpen?.timer);
    pendingOpen = {
      formId,
      timer: setTimeout(() => {
        pendingOpen = null;
        const source = findPdfSource();
        if (source) downloadPdf(source.dataset.pdfUrl);
      }, OPEN_TIMEOUT_MS),
    };
  });

  window.addEventListener('klaviyoForms', (event) => {
    const { type, formId } = event.detail || {};

    if (type === 'open' && pendingOpen?.formId === formId) {
      clearTimeout(pendingOpen.timer);
      pendingOpen = null;
      return;
    }

    if (type !== 'submit') return;

    const source = findPdfSource();
    if (!source) return;

    const formIds = (source.dataset.downloadFormIds || '').split(',').filter(Boolean);
    if (formIds.includes(formId)) downloadPdf(source.dataset.pdfUrl);
  });
}
